import { Delete, Edit } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    CircularProgress,
    IconButton,
} from "@mui/material";
import classnames from "classnames";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import { isRoleAdmin } from "../../../frappeConfig";
import arrowIcon from "../../../images/ri_arrow-up-s-line.svg";
import OrderStatusIcon from "./OrderStatusIcon";
import { useStyles } from "./styles";
import { AppContext } from "../../../AppContext";

const getHiddenBundleDefaultCodes = (menuItemBundle) => {
    if (!menuItemBundle) return new Set();
    try {
        const parsed =
            typeof menuItemBundle === "string"
                ? JSON.parse(menuItemBundle)
                : menuItemBundle;
        const categories = Array.isArray(parsed?.categories)
            ? parsed.categories
            : Array.isArray(parsed)
              ? parsed
              : [];
        const hidden = new Set();
        categories.forEach((cat) => {
            const opts = Array.isArray(cat?.options) ? cat.options : [];
            opts.forEach((opt) => {
                if (
                    opt?.item_code &&
                    opt?.default === true &&
                    opt?.highlight === "hidden"
                ) {
                    hidden.add(opt.item_code);
                }
            });
        });
        return hidden;
    } catch {
        return new Set();
    }
};

const OrderCartItem = ({
    item,
    onItemEdit,
    onDelete,
    isSaved,
    itemDeleting,
    isHighlighted,
    skipDeleteConfirm = false,
    deletingItemIdentifier,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const [expanded, setExpanded] = useState(false);

    const { userInfo, elevatedPrivileges } = useContext(AppContext);

    const [selectedOrder, setSelectedOrder] = useState(null);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteConfirmTitle, setDeleteConfirmTitle] = useState("");
    const [itemToDelete, setItemToDelete] = useState(null);

    const itemIdentifier = item?.item?.identifier || item?.entry_name || null;
    const isDeleting =
        !!deletingItemIdentifier &&
        !!itemIdentifier &&
        deletingItemIdentifier === itemIdentifier;

    const notesText = String(item?.notes || item?.item?.notes || "").trim();
    const hiddenBundleDefaultCodes = getHiddenBundleDefaultCodes(
        item?.item?.menu_item_bundle
    );
    const addonsForDisplay = Array.isArray(item?.addons)
        ? item.addons.filter(
              (a) => !hiddenBundleDefaultCodes.has(a?.item_code || "")
          )
        : [];
    const hasDetails = addonsForDisplay.length > 0 || !!notesText;

    const handleDeleteOrderItemRequest = (item) => {
        if (isDeleting) return;
        if (isSaved) {
            // Draft orders should delete items immediately (no notes/waste confirmation modal)
            if (skipDeleteConfirm) {
                onDelete?.(
                    {
                        ...item,
                        elevatedPrivileges: elevatedPrivileges,
                    },
                    undefined,
                    item.qty,
                    "",
                    false
                );
                return;
            }
            setDeleteConfirmTitle(
                t("pos.deletingItemTitle", {
                    qty: item.qty,
                    code: item.item.item_code,
                })
            );
            setItemToDelete({
                ...item,
                elevatedPrivileges: elevatedPrivileges,
            });
            setDeleteConfirmOpen(true);
        } else {
            onDelete(item);
        }
    };

    const handleDeleteConfirm = (password, qty, note, waste) => {
        onDelete(itemToDelete, password, qty, note, waste);
        setTimeout(() => {
            setDeleteConfirmOpen(false);
        }, 1000);
    };

    const handleDeleteConfirmClose = () => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
    };

    const handleOrderItemEdit = (item) => {
        onItemEdit?.(item);
    };

    const getOnChange = () => {
        if (hasDetails) {
            setExpanded(!expanded);
        } else {
            setExpanded(false);
        }
    };

    const getIcon = () => {
        let classesName = classes.Arrow_disabled;
        if (hasDetails) {
            classesName = classes.Arrow_enabled;
        }
        return (
            <div className={classesName}>
                <div>
                    <img src={arrowIcon} alt="" />
                </div>
            </div>
        );
    };

    const checkCanDelete = (item) => {
        return (
            onDelete &&
            (item.item.status === "Attending" ||
                isRoleAdmin(userInfo.role_profile_name))
        );
    };

    const checkCanEdit = (item) => {
        return onItemEdit && item.item.status === "Attending";
    };

    const getTotalPrice = (item) => {
        let addonsTotal = 0;
        let total = 0;

        total += item.qty * (item.item.rate || item.item.standard_rate);

        if (item.addons) {
            item.addons.forEach((addon) => {
                addonsTotal += addon?.standard_rate * item.qty;
            });
        }

        addonsTotal = addonsTotal || 0;

        return total + addonsTotal;
    };

    return (
        <>
            <div style={{ position: "relative", opacity: isDeleting ? 0.6 : 1 }}>
                {isDeleting && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            background: "rgba(255,255,255,0.5)",
                            zIndex: 2,
                            pointerEvents: "all",
                        }}
                    >
                        <CircularProgress size={18} />
                        <span style={{ fontSize: 12 }}>
                            {t("pos.deleting")}
                        </span>
                    </div>
                )}
                <Accordion
                    expanded={expanded}
                    // onChange={getOnChange}
                    className={classes.Accordion}
                >
                <AccordionSummary
                    aria-controls={
                        item.item.identifier || item.entry_name + "-content"
                    }
                    id={item.item.identifier || item.entry_name + "-header"}
                    expandIcon={getIcon()}
                    className={classes.AccordionSummary}
                >
                    <div
                        className={classnames("Item", {
                            IsSelected:
                                item?.item?.item_code ===
                                selectedOrder?.item?.item_code,
                            "Item--highlight": isHighlighted,
                        })}
                        onClick={() => setSelectedOrder(item)}
                    >
                        <div className="Item__Main">
                            <div className="Content" onClick={getOnChange}>
                                <div className="Img">
                                    <div
                                        className="Img"
                                        style={{
                                            width: "50px",
                                            height: "50px",
                                        }}
                                    >
                                        {item && item?.item?.item_code && (
                                            <img
                                                src={item?.item?.cover}
                                                alt={item?.item?.item_name || ""}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                }}
                                            />
                                        )}{" "}
                                    </div>
                                </div>
                                <div className="Name">
                                    <div>{item.item.item_name}</div>
                                    <div>x {item.qty}</div>
                                </div>
                                <div className="Price">
                                    NIS {getTotalPrice(item)}
                                </div>
                            </div>
                            <div className="Item_2">
                                <div className="Item__Secondary">
                                    <div className="PostActions">
                                        <div className="ItemDetails">
                                            <OrderStatusIcon
                                                status={item.item.status}
                                            />
                                        </div>
                                        {checkCanDelete(item) && (
                                            <IconButton
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleDeleteOrderItemRequest(
                                                        item
                                                    );
                                                }}
                                                disabled={isDeleting}
                                            >
                                                {isDeleting ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    <Delete
                                                        fontSize="small"
                                                        className={
                                                            isSaved
                                                                ? classes.Table_edit_order_right_saved
                                                                : classes.Table_edit_order_right
                                                        }
                                                    />
                                                )}
                                            </IconButton>
                                        )}
                                        {checkCanEdit(item) && (
                                            <IconButton
                                                onClick={() =>
                                                    handleOrderItemEdit(item)
                                                }
                                                disabled={isDeleting}
                                            >
                                                <Edit
                                                    className={
                                                        isSaved
                                                            ? classes.Table_edit_order_right_saved
                                                            : classes.Table_edit_order_right
                                                    }
                                                />
                                            </IconButton>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </AccordionSummary>

                <AccordionDetails style={{ padding: 0 }}>
                    <div className="Item">
                        <div className="Item__Secondary">
                            {addonsForDisplay.map((i) => (
                                <div
                                    key={
                                        "ItemAddons_" +
                                        (item.item.name || "") +
                                        "_" +
                                        (i?.item_code || "")
                                    }
                                >
                                    <span>- {i?.item_code}</span>
                                    <span>+ {i?.standard_rate}</span>
                                </div>
                            ))}

                            {!!notesText && (
                                <div style={{ marginTop: 6 }}>
                                    <div style={{ whiteSpace: "pre-wrap" }}>
                                        {notesText}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </AccordionDetails>
                </Accordion>
            </div>
            <ConfirmationDialog
                open={deleteConfirmOpen}
                onClose={handleDeleteConfirmClose}
                onConfirm={handleDeleteConfirm}
                item={itemToDelete}
                title={deleteConfirmTitle}
                loading={itemDeleting}
                addQtyField={item.qty}
                showWasteToggle={true}
            />
        </>
    );
};

export default OrderCartItem;
